import { LucideX } from "lucide-react";

/**
 * Reusable modal with backdrop, close button, and optional icon header.
 *
 * @param {object} props
 * @param {boolean} props.open - Whether the modal is visible
 * @param {() => void} props.onClose - Called when backdrop or close button clicked
 * @param {string} [props.title] - Modal title text
 * @param {string} [props.subtitle] - Optional subtitle below the title
 * @param {import("react").ReactNode} [props.icon] - Icon element for the header
 * @param {string} [props.maxWidth] - Tailwind max-width class (default "max-w-md")
 * @param {string} [props.maxHeight] - Tailwind max-height class (default "max-h-[80vh]")
 * @param {number} [props.zIndex] - z-index level (default 50)
 * @param {boolean} [props.preventClose] - When true, backdrop and close button are disabled
 * @param {import("react").ReactNode} props.children - Modal body content
 */
export function Modal({
  open,
  onClose,
  title,
  subtitle,
  icon,
  maxWidth = "max-w-md",
  maxHeight = "max-h-[80vh]",
  zIndex = 50,
  preventClose = false,
  children,
}) {
  if (!open) return null;

  function handleBackdropClick() {
    if (!preventClose) onClose();
  }

  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex }}
    >
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={handleBackdropClick}
      />
      <div
        className={`relative bg-card border border-border rounded-2xl shadow-2xl w-full ${maxWidth} flex flex-col ${maxHeight}`}
      >
        {/* Header */}
        {(title || icon) && (
          <>
            <div className="flex items-start justify-between px-6 pt-6 pb-4 shrink-0">
              <div className="flex items-center gap-3">
                {icon && (
                  <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                    {icon}
                  </div>
                )}
                <div>
                  {title && (
                    <p className="text-base font-semibold text-foreground leading-tight">
                      {title}
                    </p>
                  )}
                  {subtitle && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {subtitle}
                    </p>
                  )}
                </div>
              </div>
              {!preventClose && (
                <button
                  onClick={onClose}
                  className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors mt-0.5 shrink-0"
                >
                  <LucideX size={15} />
                </button>
              )}
            </div>
            <div className="h-px bg-border mx-6 shrink-0" />
          </>
        )}

        {children}
      </div>
    </div>
  );
}

export function ModalBody({ children, className = "" }) {
  return (
    <div className={`flex-1 overflow-y-auto min-h-0 px-6 py-5 ${className}`}>
      {children}
    </div>
  );
}

export function ModalFooter({ children, className = "" }) {
  return (
    <div className={`shrink-0 px-6 pb-5 pt-4 border-t border-border flex gap-2 ${className}`}>
      {children}
    </div>
  );
}
